import { ChangeEvent, FC } from 'react';
import {
  DateRangePicker,
  Input,
  Label,
  SearchIcon,
  Select,
  Table,
  TableActionHeader,
  TDatesRange,
  TExtendedColumn,
  TSelectItem,
  Button,
} from '@sim-admin-frontends/ui-shared';
import { ProcessingStatus, TPaymentsData } from '@sim-admin-frontends/data-access';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDebounce } from '@sim-admin-frontends/utils-shared';

const Header = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spaces.spacing16};
  align-items: center;
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${({ theme }) => theme.spaces.spacing4};
`;

export interface PaymentsViewProps {
  data: TPaymentsData;
  columns: TExtendedColumn<TPaymentsData[number]>[];
  onNextPress: () => void;
  onPreviousPress: () => void;
  pageIndex: number;
  itemsCount: number;
  loading: boolean;
  hasNextPage: boolean;
  tablePageSize: number;
  onDateFilterChange: (filter: TDatesRange) => void;
  initialDateFilterValues: TDatesRange;
  onStatusFilterChange: (option: readonly TSelectItem[] | null) => void;
  onIdentifierTermChange: (identifierTerm: string) => void;
  onDownloadCsv: () => void;
  isDownloading: boolean;
}

const PaymentsView: FC<PaymentsViewProps> = ({
  data,
  columns,
  onNextPress,
  pageIndex,
  onPreviousPress,
  itemsCount,
  loading,
  hasNextPage,
  tablePageSize,
  onDateFilterChange,
  initialDateFilterValues,
  onStatusFilterChange,
  onIdentifierTermChange,
  onDownloadCsv,
  isDownloading,
}) => {
  const { t } = useTranslation();

  const onIdentifierTermChangeInternal = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onIdentifierTermChange(value);
  };

  const debouncedOnIdentifierTermChange = useDebounce(onIdentifierTermChangeInternal, 500);

  return (
    <>
      <TableActionHeader
        prependItem={
          <Header>
            <DateRangePicker
              dateFromLabel={t('payments.dateFrom')}
              dateToLabel={t('payments.dateTo')}
              onDatesChanged={onDateFilterChange}
              initialValues={initialDateFilterValues}
              testId="PaymentsView#DatePicker"
            />
            <div>
              <StyledLabel>{t('payments.identifier')}</StyledLabel>
              <Input
                testId="PaymentsView#Search"
                icon={<SearchIcon />}
                onChange={debouncedOnIdentifierTermChange}
              />
            </div>
          </Header>
        }
      >
        <Button onClick={onDownloadCsv} variant="dark" isBoxy disabled={isDownloading}>
          {t('payments.downloadCsv')}
        </Button>
        <Select
          options={Object.values(ProcessingStatus).map((value) => ({ label: value, value: value }))}
          onChange={onStatusFilterChange}
          defaultValue={{ label: ProcessingStatus.New, value: ProcessingStatus.New }}
          clearable={false}
          testId="PaymentsView#Select-status"
        />
      </TableActionHeader>
      <Table
        tablePageSize={tablePageSize}
        loading={loading}
        itemsCount={itemsCount}
        pageIndex={pageIndex}
        data={data}
        columns={columns}
        hasPagination={true}
        onNextPress={onNextPress}
        hasNextPage={hasNextPage}
        onPreviousPress={onPreviousPress}
        testId="PaymentsView#Table"
      />
    </>
  );
};

export default PaymentsView;
